import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import DocumentViewer from './components/DocumentViewer';
import SignatureWorkflow from './components/SignatureWorkflow';
import DocumentAnnotations from './components/DocumentAnnotations';
import SignatureHistory from './components/SignatureHistory';

const DigitalSignature = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('workflow');
  const [document, setDocument] = useState(null);
  const [signatures, setSignatures] = useState([]);
  const [annotations, setAnnotations] = useState([]);
  const [stakeholders, setStakeholders] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Initialize mock data
    const initializeData = () => {
      const mockDocument = {
        id: 'doc-001',
        name: 'Property Purchase Agreement - 123 Blockchain Avenue',
        type: 'purchase_agreement',
        size: '2.4 MB',
        totalPages: 12,
        status: 'pending_signature',
        createdAt: '2025-01-20T10:00:00Z',
        updatedAt: '2025-01-21T09:45:03Z',
        hash: '0xa1b2c3d4e5f6789012345678901234567890abcdef',
        url: '/documents/property-purchase-agreement.pdf'
      };

      const mockStakeholders = [
        {
          id: 'user-1',
          name: 'John Smith',
          role: 'Property Owner',
          email: 'john.smith@email.com',
          avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
          signedAt: '2025-01-20T14:30:00Z',
          order: 1
        },
        {
          id: 'user-2',
          name: 'Sarah Johnson',
          role: 'Legal Representative',
          email: 'sarah.johnson@lawfirm.com',
          avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
          signedAt: '2025-01-20T15:45:00Z',
          order: 2
        },
        {
          id: 'user-3',
          name: 'Michael Davis',
          role: 'Notary Public',
          email: 'michael.davis@notary.com',
          avatar: 'https://randomuser.me/api/portraits/men/56.jpg',
          signedAt: null,
          order: 3
        },
        {
          id: 'user-4',
          name: 'Emily Chen',
          role: 'Property Engineer',
          email: 'emily.chen@engineering.com',
          avatar: 'https://randomuser.me/api/portraits/women/68.jpg',
          signedAt: null,
          order: 4
        },
        {
          id: 'user-5',
          name: 'Robert Wilson',
          role: 'Financial Advisor',
          email: 'robert.wilson@finance.com',
          avatar: 'https://randomuser.me/api/portraits/men/78.jpg',
          signedAt: null,
          order: 5
        }
      ];

      const mockCurrentUser = {
        id: 'user-3',
        name: 'Michael Davis',
        role: 'Notary Public',
        email: 'michael.davis@notary.com',
        avatar: 'https://randomuser.me/api/portraits/men/56.jpg'
      };

      const mockSignatures = [
        {
          id: 1,
          userId: 'user-1',
          userName: 'John Smith',
          page: 3,
          x: 70,
          y: 85,
          timestamp: '2025-01-20T14:30:00Z',
          blockchainHash: '0xa1b2c3d4e5f6789012345678901234567890abcdef1234567890abcdef123456'
        },
        {
          id: 2,
          userId: 'user-2',
          userName: 'Sarah Johnson',
          page: 5,
          x: 30,
          y: 90,
          timestamp: '2025-01-20T15:45:00Z',
          blockchainHash: '0xb2c3d4e5f6789012345678901234567890abcdef1234567890abcdef1234567'
        }
      ];

      const mockAnnotations = [
        {
          id: 1,
          userId: 'user-2',
          userName: 'Sarah Johnson',
          userAvatar: 'https://randomuser.me/api/portraits/women/44.jpg',
          content: 'Please verify the property tax assessment details on this page before signing.',
          page: 3,
          timestamp: '2025-01-20T13:15:00Z',
          type: 'comment'
        },
        {
          id: 2,
          userId: 'user-1',
          userName: 'John Smith',
          userAvatar: 'https://randomuser.me/api/portraits/men/32.jpg',
          content: 'The closing date should be updated to reflect the new timeline we discussed.',
          page: 8,
          timestamp: '2025-01-20T16:30:00Z',
          type: 'question'
        },
        {
          id: 3,
          userId: 'user-4',
          userName: 'Emily Chen',
          userAvatar: 'https://randomuser.me/api/portraits/women/68.jpg',
          content: 'Engineering report has been attached as Appendix C. All structural assessments are complete.',
          page: 12,
          timestamp: '2025-01-21T08:45:00Z',
          type: 'comment'
        }
      ];

      setDocument(mockDocument);
      setStakeholders(mockStakeholders);
      setCurrentUser(mockCurrentUser);
      setSignatures(mockSignatures);
      setAnnotations(mockAnnotations);
      setIsLoading(false);
    };

    initializeData();
  }, []);

  const handleSignDocument = (signatureData) => {
    const newSignature = {
      id: Date.now(),
      ...signatureData,
      userName: currentUser?.name,
      blockchainHash: `0x${Math.random()?.toString(16)?.substr(2, 64)}`
    };

    setSignatures(prev => [...prev, newSignature]);
    
    // Update stakeholder status
    setStakeholders(prev => 
      prev?.map(stakeholder => 
        stakeholder?.id === currentUser?.id 
          ? { ...stakeholder, signedAt: signatureData?.timestamp }
          : stakeholder
      )
    );

    // Check if all signatures are complete
    const updatedStakeholders = stakeholders?.map(s => 
      s?.id === currentUser?.id ? { ...s, signedAt: signatureData?.timestamp } : s
    );
    
    const allSigned = updatedStakeholders?.every(s => s?.signedAt);
    if (allSigned) {
      setDocument(prev => ({ ...prev, status: 'completed' }));
    }
  };

  const handleRejectDocument = (rejectionData) => {
    setStakeholders(prev => 
      prev?.map(stakeholder => 
        stakeholder?.id === currentUser?.id 
          ? { ...stakeholder, rejectedAt: rejectionData?.timestamp, rejectionReason: rejectionData?.reason }
          : stakeholder
      )
    );
    
    setDocument(prev => ({ ...prev, status: 'rejected' }));
  };

  const handleSignaturePlace = (signatureData) => {
    // This would typically trigger the signature workflow
    console.log('Signature placed:', signatureData);
  };

  const handleAddAnnotation = (annotation) => {
    setAnnotations(prev => [...prev, annotation]);
  };

  const handleDownloadDocument = () => {
    // Simulate document download
    const link = document?.createElement('a');
    link.href = '#';
    link.download = document?.name || 'document.pdf';
    link?.click();
  };

  const handleCompleteWorkflow = () => {
    navigate('/transaction-details', { 
      state: { message: 'Document signed successfully and recorded on blockchain' }
    });
  };

  const tabs = [
    { id: 'workflow', label: 'Signature Workflow', icon: 'PenTool' },
    { id: 'comments', label: 'Comments', icon: 'MessageCircle' },
    { id: 'history', label: 'History', icon: 'Clock' }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumbs />
        <div className="flex items-center justify-center h-96">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="text-muted-foreground">Loading document...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Breadcrumbs />
      <div className="pt-20">
        {/* Document Header */}
        <div className="bg-card border-b border-border px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="FileSignature" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">{document?.name}</h1>
                <div className="flex items-center space-x-4 mt-1 text-sm text-muted-foreground">
                  <span>Document ID: {document?.id}</span>
                  <span>•</span>
                  <span>Size: {document?.size}</span>
                  <span>•</span>
                  <span>Pages: {document?.totalPages}</span>
                  <span>•</span>
                  <div className="flex items-center space-x-1">
                    <Icon name="Shield" size={14} className="text-success" />
                    <span>Blockchain Secured</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                document?.status === 'completed' ? 'bg-success/20 text-success' :
                document?.status === 'rejected'? 'bg-error/20 text-error' : 'bg-warning/20 text-warning'
              }`}>
                {document?.status === 'completed' ? 'All Signatures Complete' :
                 document?.status === 'rejected'? 'Document Rejected' : 'Pending Signatures'}
              </div>

              <Button
                variant="outline"
                onClick={handleDownloadDocument}
                iconName="Download"
                iconPosition="left"
              >
                Download
              </Button>

              {document?.status === 'completed' && (
                <Button
                  variant="default"
                  onClick={handleCompleteWorkflow}
                  iconName="CheckCircle"
                  iconPosition="left"
                >
                  Complete Workflow
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex h-[calc(100vh-200px)]">
          {/* Document Viewer */}
          <div className="flex-1 p-6">
            <DocumentViewer
              document={document}
              signatures={signatures}
              currentUser={currentUser}
              onSignaturePlace={handleSignaturePlace}
            />
          </div>

          {/* Right Panel */}
          <div className="w-96 border-l border-border bg-muted/30">
            {/* Tab Navigation */}
            <div className="flex border-b border-border bg-card">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 text-sm font-medium transition-smooth ${
                    activeTab === tab?.id
                      ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  <span>{tab?.label}</span>
                  {tab?.id === 'comments' && annotations?.length > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 text-xs bg-accent text-accent-foreground rounded-full">
                      {annotations?.length}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="h-full">
              {activeTab === 'workflow' && (
                <SignatureWorkflow
                  document={document}
                  stakeholders={stakeholders}
                  currentUser={currentUser}
                  onSignDocument={handleSignDocument}
                  onRejectDocument={handleRejectDocument}
                />
              )}

              {activeTab === 'comments' && (
                <DocumentAnnotations
                  annotations={annotations}
                  currentUser={currentUser}
                  onAddAnnotation={handleAddAnnotation}
                />
              )}

              {activeTab === 'history' && (
                <SignatureHistory
                  signatures={signatures}
                  document={document}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DigitalSignature;